// =============================================================================
// Drilldown — do número até o lançamento de origem (AF-01 §9)
//
// Duas coisas que esta tela precisa deixar visíveis e que não são óbvias:
//
// 1. Em lançamento rateado, o gestor vê o que caiu NO CC DELE, não o valor
//    cheio. Por isso a view devolve `valor_rateado_centavos` e
//    `valor_total_centavos` separados (contrato §2).
// 2. Sugestão de baixa confiança do matching por texto NUNCA se confirma
//    sozinha. Aqui ela aparece marcada e, para o Financeiro, com o botão de
//    confirmar ou trocar de linha (migration 036) — decisão dele, não do motor.
//    Até 18/09 esta tela mandava fazer isso na Central Operacional, e a
//    Central mandava fazer aqui: não havia botão em lugar nenhum.
//
// Pedido do Financeiro de 20/10 (itens 1.1 e 1.2): cada lançamento diz como
// entrou — automático (o motor confirmou sozinho), confirmado pelo Financeiro,
// aguardando confirmação, ou sem linha orçada —, e dá para filtrar, com o que
// espera confirmação primeiro. "Automático" é confirmado SEM pessoa
// (`confirmado_por_pessoa`, migration 064). Até aqui, lançamento sem alocação
// saía com a pílula "Sugestão por texto", que é o oposto do que ele é.
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  api,
  buscarLancamentosDaCoordenada,
  buscarLancamentosDaLinha,
  type Lancamento,
  type LinhaMes,
} from "../lib/api.ts";
import { competenciaLonga, dataCurta, moeda, percentual } from "../lib/formato.ts";
import { Modal } from "../componentes/Modal.tsx";
import { Pilula, Vazio } from "../componentes/ui.tsx";
import { ModalAlocacao } from "./ModalAlocacao.tsx";

type Situacao = "aguardando" | "sem_linha" | "automatico" | "pessoa" | "total";

/** Antes da 064 a API não mandava `confirmado_por_pessoa`: aí vale o método,
 *  que é como esta tela decidia antes. */
function situacao(l: Lancamento): Situacao {
  if (l.lancamento_id === null) return "total";
  if (l.estado_alocacao === "sugerida") return "aguardando";
  if (l.estado_alocacao === "confirmada") {
    const pessoa = l.confirmado_por_pessoa ?? (l.metodo === "manual" || l.metodo === "texto");
    return pessoa ? "pessoa" : "automatico";
  }
  return "sem_linha";
}

const SITUACOES: { id: Situacao; rotulo: string }[] = [
  { id: "aguardando", rotulo: "Aguarda confirmação" },
  { id: "sem_linha", rotulo: "Sem linha orçada" },
  { id: "automatico", rotulo: "Automático" },
  { id: "pessoa", rotulo: "Confirmado pelo Financeiro" },
];
const ORDEM: Record<Situacao, number> = { aguardando: 0, sem_linha: 1, automatico: 2, pessoa: 3, total: 4 };

/** Como a compra foi dividida: entre categorias, entre centros de custo (a
 *  categoria rateada entre áreas, 27/10), ou os dois. */
function comoDividida(partes: number, categorias: number): string {
  if (categorias >= partes) return `dividida em ${partes} categorias`;
  if (categorias <= 1) return `dividida entre ${partes} centros de custo`;
  return `dividida em ${partes} partes, entre ${categorias} categorias e mais de um centro de custo`;
}

export function ModalLancamentos({
  linha,
  onFechar,
}: {
  /** ⚠️ `linha_id` tem de existir: o drilldown é de um mês. Com período de
   *  vários meses a API devolve id nulo, e quem abre o modal precisa conferir
   *  antes — é o que a tabela da Planilha e a do Painel fazem.
   *
   *  Na variante por coordenada, `centro_custo_id` nulo abre a categoria em
   *  todos os centros de custo (pedido de 20/10, item 1.4). */
  linha: (LinhaMes & { linha_id: string }) | {
    centro_custo_id: string | null;
    centro_custo_codigo: string;
    centro_custo_nome: string;
    categoria_id: string | null;
    categoria_nome: string;
    competencia: string;
    orcado_centavos: string;
    realizado_centavos: string;
    pct_execucao: string;
  };
  onFechar: () => void;
}) {
  const porLinha = "linha_id" in linha;
  const todosOsCcs = !porLinha && linha.centro_custo_id === null;
  const q = useQuery({
    queryKey: porLinha
      ? ["lancamentos", linha.linha_id]
      : ["lancamentos-coordenada", linha.centro_custo_id, linha.categoria_id, linha.competencia],
    queryFn: () => porLinha
      ? buscarLancamentosDaLinha(linha.linha_id)
      : buscarLancamentosDaCoordenada(
          linha.centro_custo_id,
          linha.categoria_id,
          linha.competencia,
        ),
  });
  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<{ papeis: string[] }>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  // Só decide se o botão aparece; quem recusa o gestor é a RPC.
  const decide = (eu.data?.papeis ?? []).some((p) => p === "financeiro" || p === "admin");
  const [decidindo, setDecidindo] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Situacao | "todos">("todos");

  // Um modal por vez: a decisão ocupa o lugar do drilldown e, ao fechar, ele volta.
  if (decidindo) {
    return <ModalAlocacao lancamentoId={decidindo} onFechar={() => setDecidindo(null)} />;
  }

  const todos = q.data?.lancamentos ?? [];
  const contagem = new Map<Situacao, number>();
  for (const l of todos) contagem.set(situacao(l), (contagem.get(situacao(l)) ?? 0) + 1);
  // O que espera confirmação primeiro; dentro de cada situação, a ordem da API
  // (maior valor primeiro). `sort` é estável.
  const visiveis = todos
    .filter((l) => filtro === "todos" || situacao(l) === filtro)
    .sort((a, b) => ORDEM[situacao(a)] - ORDEM[situacao(b)]);

  return (
    <Modal titulo={linha.categoria_nome} onFechar={onFechar} largura="max-w-3xl">
      <p className="text-[12.5px] text-tinta-media">
        {todosOsCcs ? "Todos os centros de custo" : `${linha.centro_custo_codigo} · ${linha.centro_custo_nome}`} ·{" "}
        {competenciaLonga(linha.competencia)}
      </p>
      <p className="num mt-1 text-[13.5px]">
        <strong className="font-semibold">{moeda(linha.realizado_centavos)}</strong> de{" "}
        {moeda(linha.orcado_centavos)} orçado · {percentual(linha.pct_execucao)}
      </p>

      {/* Os filtros só aparecem quando há mais de uma situação para separar. */}
      {contagem.size > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Chip ativo={filtro === "todos"} onClick={() => setFiltro("todos")}>Todos · {todos.length}</Chip>
          {SITUACOES.filter((s) => contagem.get(s.id)).map((s) => (
            <Chip key={s.id} ativo={filtro === s.id} onClick={() => setFiltro(s.id)}>
              {s.rotulo} · {contagem.get(s.id)}
            </Chip>
          ))}
        </div>
      )}

      <div className="mt-4">
        {q.isPending && <p className="py-8 text-center text-[13px] text-tinta-fraca">Carregando…</p>}

        {q.isError && (
          <Vazio
            titulo="Não foi possível carregar os lançamentos"
            dica={(q.error as { message?: string })?.message}
          />
        )}

        {q.data && todos.length === 0 && (
          porLinha ? (
            <Vazio
              titulo="Nenhum lançamento alocado ainda"
              dica="A linha está orçada, mas nenhum lançamento do Conta Azul casou com ela nesta competência. É o estado Provisionado."
            />
          ) : (
            <Vazio
              titulo="Nenhum lançamento nesta categoria e mês"
              dica="O Conta Azul não tem lançamento desta categoria nesta competência para os centros de custo que você vê."
            />
          )
        )}

        {q.data && todos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse text-[13px]">
              <thead>
                <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">
                    Lançamento
                  </th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">
                    Fornecedor
                  </th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">
                    Venc. · Pgto.
                  </th>
                  <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">
                    Valor
                  </th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">
                    Conciliação
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((l, i) => {
                  const rateado = l.valor_rateado_centavos !== l.valor_total_centavos;
                  const s = situacao(l);
                  return (
                    <tr key={l.lancamento_id ? `${l.lancamento_id}|${l.centro_custo_id}` : `total-${i}`} className="align-top">
                      <td className="border-t border-linha px-2 py-2">
                        <span className={l.ausente_na_origem_em ? "line-through opacity-60" : ""}>
                          {l.descricao || "— sem descrição —"}
                        </span>
                        {todosOsCcs && l.centro_custo_codigo && (
                          <span className="mt-0.5 block text-[11.5px] text-tinta-fraca">
                            {l.centro_custo_codigo} · {l.centro_custo_nome}
                          </span>
                        )}
                        {l.ausente_na_origem_em && (
                          <span className="mt-1 block text-[11.5px] text-tinta-fraca">
                            Sumiu da origem — não compõe mais o realizado
                          </span>
                        )}
                      </td>
                      <td className="border-t border-linha px-2 py-2 text-tinta-media">
                        {l.fornecedor_nome ?? "—"}
                      </td>
                      <td className="num border-t border-linha px-2 py-2 text-tinta-media">
                        {dataCurta(l.data_vencimento)} · {dataCurta(l.data_pagamento)}
                      </td>
                      <td className="num border-t border-linha px-2 py-2 text-right font-semibold">
                        {moeda(l.valor_rateado_centavos)}
                        {rateado && (
                          <span
                            className="block text-[11.5px] font-normal text-tinta-fraca"
                            title={todosOsCcs
                              ? "Este lançamento é rateado entre centros de custo. Cada parte aparece numa linha."
                              : "Este lançamento é rateado entre centros de custo. Você vê a parte do seu."}
                          >
                            de {moeda(l.valor_total_centavos)} rateado
                          </span>
                        )}
                        {(l.compra_partes ?? 0) > 1 && l.compra_total_centavos && (
                          <span
                            className="block text-[11.5px] font-normal text-tinta-fraca"
                            title="O lançamento foi dividido no Conta Azul, entre categorias ou centros de custo. Cada parte aparece no seu lugar; aqui está o total, para conferir com o Conta Azul."
                          >
                            parte de uma compra de {moeda(l.compra_total_centavos)} {comoDividida(l.compra_partes!, l.compra_categorias ?? l.compra_partes!)}
                          </span>
                        )}
                      </td>
                      <td className="border-t border-linha px-2 py-2">
                        <PilulaSituacao l={l} s={s} />
                        {decide && s === "aguardando" && l.lancamento_id && !l.ausente_na_origem_em && (
                          <button type="button" onClick={() => setDecidindo(l.lancamento_id)}
                            className="mt-1.5 block rounded-lg border border-laranja px-2 py-1 text-[12px] font-semibold whitespace-nowrap text-laranja-tinta hover:bg-laranja/10">
                            Confirmar ou trocar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 border-t border-linha pt-3 text-[12px] text-tinta-fraca">
        “Automático” é o que o motor confirmou sozinho: centro de custo, categoria e mês batem com uma linha só, ou há uma regra do Financeiro. Sugestão por texto nunca se confirma sozinha: o Financeiro confirma ou troca de linha, aqui ou na Central Operacional.
      </p>
    </Modal>
  );
}

function PilulaSituacao({ l, s }: { l: Lancamento; s: Situacao }) {
  if (s === "total") return <span className="text-tinta-fraca">—</span>;
  if (s === "aguardando") {
    return (
      <>
        <Pilula tom="atencao">Aguarda confirmação</Pilula>
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          {l.metodo === "texto" ? "Sugestão pela descrição" : "Sugestão do motor"}
        </span>
      </>
    );
  }
  if (s === "sem_linha") {
    return (
      <span title="Não há linha orçada neste centro de custo, categoria e mês — é o “não orçado” — ou o lançamento ainda não foi alocado">
        <Pilula tom="neutro">Sem linha orçada</Pilula>
      </span>
    );
  }
  if (s === "automatico") {
    return (
      <>
        <Pilula tom="ok">Automático</Pilula>
        {l.metodo === "regra" && (
          <span className="mt-1 block text-[11.5px] text-tinta-fraca">Por regra do Financeiro</span>
        )}
      </>
    );
  }
  return (
    <>
      <Pilula tom="ok">Confirmado pelo Financeiro</Pilula>
      {l.metodo === "manual" && <span className="mt-1 block text-[11.5px] text-tinta-fraca">Alocado à mão</span>}
    </>
  );
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1 text-[12px] font-semibold ${
        ativo ? "border-azul bg-azul text-white" : "border-linha bg-superficie text-tinta-media hover:bg-superficie-2"
      }`}
    >
      {children}
    </button>
  );
}
